import { useEffect, useState } from "react";
import {
  FaPaperPlane,
  FaSpinner,
  FaCheck,
  FaExclamationTriangle,
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa";
import "./GoogleForm.css";

const FEEDBACK_SECONDS = 30;

const chunkFields = (fields, fieldsPerPage) => {
  if (!fieldsPerPage || fieldsPerPage >= fields.length) {
    return [fields];
  }

  const pages = [];

  for (let i = 0; i < fields.length; i += fieldsPerPage) {
    pages.push(fields.slice(i, i + fieldsPerPage));
  }

  return pages;
};

const isFieldFilled = (field, value) => {
  if (!field.required) return true;

  if (field.type === "checkbox") {
    return Array.isArray(value) && value.length > 0;
  }

  return value !== undefined && value !== null && String(value).trim() !== "";
};

const GoogleForm = ({
  formUrl,
  notifyUrl,
  emailSubject = "Novo contato via site",
  fields = [],
  hiddenFields = [],
  fieldsPerPage,
  submitText = "Enviar formulário",
  sendingText = "Enviando...",
  successMessage = "Formulário enviado com sucesso!",
  receivedMessage = "Formulário recebido! Entraremos em contato em breve.",
  privacyText = "Concordo em receber comunicações e aceito os termos de privacidade.",
  showPrivacy = true,
  backText = "Voltar",
  nextText = "Próximo",
  className = "",
}) => {
  const pages = chunkFields(fields, fieldsPerPage);
  const isPaginated = pages.length > 1;

  const createInitialData = () =>
    fields.reduce((acc, field) => {
      acc[field.name] = field.type === "checkbox" ? [] : "";
      return acc;
    }, {});

  const [formData, setFormData] = useState(createInitialData);
  const [currentPage, setCurrentPage] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  useEffect(() => {
    if (countdown === 0 && feedback) {
      setFeedback(null);
    }
  }, [countdown, feedback]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData((prev) => {
        const currentValues = Array.isArray(prev[name]) ? prev[name] : [];

        return {
          ...prev,
          [name]: checked
            ? [...currentValues, value]
            : currentValues.filter((item) => item !== value),
        };
      });

      return;
    }

    const field = fields.find((f) => f.name === name);
    const nextValue = field?.format ? field.format(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: nextValue,
    }));
  };

  const isPageComplete = (pageIndex) =>
    pages[pageIndex].every((field) =>
      isFieldFilled(field, formData[field.name])
    );

  const goToNextPage = () => {
    if (!isPageComplete(currentPage)) return;

    setCurrentPage((page) => Math.min(page + 1, pages.length - 1));
  };

  const goToPreviousPage = () => {
    setCurrentPage((page) => Math.max(page - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPaginated && currentPage < pages.length - 1) {
      goToNextPage();
      return;
    }

    if (!formUrl) {
      setFeedback({
        type: "received",
        message: "Formulário configurado incorretamente.",
      });

      return;
    }

    setIsSending(true);
    setFeedback(null);

    try {
      const body = new URLSearchParams();

      fields.forEach((field) => {
        const value = formData[field.name];
        const googleName = field.googleName || field.name;

        if (field.type === "checkbox") {
          if (Array.isArray(value)) {
            value.forEach((item) => {
              body.append(googleName, item);
            });
          }

          return;
        }

        body.append(googleName, value || "");
      });

      hiddenFields.forEach((field) => {
        if (!field.googleName) return;

        body.append(field.googleName, field.value ?? "");
      });

      await fetch(formUrl, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: body.toString(),
      });

      if (notifyUrl) {
        const emailData = fields.reduce(
          (acc, field) => {
            const value = formData[field.name];

            acc[field.label || field.name] = Array.isArray(value)
              ? value.join(", ")
              : value || "";

            return acc;
          },
          {
            _subject: emailSubject,
            _template: "box",
          }
        );

        hiddenFields.forEach((field) => {
          emailData[field.label || field.name] = field.value ?? "";
        });

        const emailResponse = await fetch(notifyUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(emailData),
        });

        const emailResult = await emailResponse.json();

        if (!emailResult.success) {
          throw new Error("Falha no envio do e-mail");
        }
      }

      setFeedback({
        type: "success",
        message: successMessage,
      });

      setFormData(createInitialData());
      setPrivacyAccepted(false);
      setCurrentPage(0);
    } catch (error) {
      console.error("Erro no envio:", error);

      setFeedback({
        type: "received",
        message: receivedMessage,
      });
    } finally {
      setIsSending(false);
      setCountdown(FEEDBACK_SECONDS);
    }
  };

  const isLocked = isSending || countdown > 0;
  const isLastPage = currentPage === pages.length - 1;

  const renderField = (field) => {
    const Icon = field.icon;
    const id = field.id || field.name;

    if (field.type === "radio" || field.type === "checkbox") {
      const selectedValues = Array.isArray(formData[field.name])
        ? formData[field.name]
        : [];

      return (
        <div
          className={`google-form-group google-form-options ${
            field.type === "checkbox"
              ? "google-form-checkboxes"
              : "google-form-radios"
          }`}
          key={field.name}
        >
          <label className="google-form-options-label">
            {Icon && <Icon />}
            <span>
              {field.label}
              {field.required && " *"}
            </span>
          </label>

          <div className="google-form-options-list">
            {field.options?.map((option, index) => {
              const optionId = `${id}-${index}`;

              const checked =
                field.type === "radio"
                  ? formData[field.name] === option
                  : selectedValues.includes(option);

              return (
                <div className="google-form-option" key={option}>
                  <input
                    type={field.type}
                    id={optionId}
                    name={field.name}
                    value={option}
                    checked={checked}
                    onChange={handleChange}
                    disabled={isLocked}
                    required={
                      field.required && field.type === "radio"
                        ? index === 0
                        : false
                    }
                  />

                  <label htmlFor={optionId}>{option}</label>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    const commonProps = {
      id,
      name: field.name,
      value: formData[field.name] || "",
      onChange: handleChange,
      required: field.required ?? false,
      placeholder: field.placeholder || "",
      disabled: isLocked,
      autoComplete: field.autoComplete || "off",
      maxLength: field.maxLength,
      minLength: field.minLength,
      inputMode: field.inputMode,
    };

    return (
      <div className="google-form-group" key={field.name}>
        <label htmlFor={id}>
          {Icon && <Icon />}
          <span>
            {field.label}
            {field.required && " *"}
          </span>
        </label>

        {field.type === "textarea" ? (
          <textarea {...commonProps} rows={field.rows || 5} />
        ) : (
          <input {...commonProps} type={field.type || "text"} />
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className={`google-form ${className}`}>
      {isPaginated && (
        <div className="google-form-steps">
          {pages.map((_, index) => (
            <span
              key={index}
              className={`google-form-step ${
                index === currentPage ? "active" : ""
              } ${index < currentPage ? "done" : ""}`}
            />
          ))}
        </div>
      )}

      {pages[currentPage].map(renderField)}

      {isLastPage && showPrivacy && (
        <div className="google-form-privacy">
          <input
            type="checkbox"
            id={`privacy-${className || "form"}`}
            checked={privacyAccepted}
            onChange={(e) => setPrivacyAccepted(e.target.checked)}
            required
            disabled={isLocked}
          />

          <label htmlFor={`privacy-${className || "form"}`}>
            {privacyText}
          </label>
        </div>
      )}

      <div
        className={`google-form-actions ${isPaginated ? "paginated" : ""}`}
      >
        {isPaginated && currentPage > 0 && (
          <button
            type="button"
            className="google-form-back"
            onClick={goToPreviousPage}
            disabled={isLocked}
          >
            <FaArrowLeft />
            <span>{backText}</span>
          </button>
        )}

        {isPaginated && !isLastPage ? (
          <button
            type="button"
            className="google-form-next"
            onClick={goToNextPage}
            disabled={isLocked || !isPageComplete(currentPage)}
          >
            <span>{nextText}</span>
            <FaArrowRight />
          </button>
        ) : (
          <button
            type="submit"
            className={`google-form-submit ${feedback ? feedback.type : ""}`}
            disabled={isLocked || (showPrivacy && !privacyAccepted)}
          >
            <span>
              {isSending
                ? sendingText
                : feedback
                ? feedback.message
                : submitText}
            </span>

            {isSending ? (
              <FaSpinner className="google-form-spin" />
            ) : feedback ? (
              <>
                {feedback.type === "success" ? (
                  <FaCheck />
                ) : (
                  <FaExclamationTriangle />
                )}

                <span className="google-form-countdown">
                  {countdown}s
                </span>
              </>
            ) : (
              <FaPaperPlane />
            )}
          </button>
        )}
      </div>
    </form>
  );
};

export default GoogleForm;